/** Query keys Firebase appends to the email-link continue URL. Keep `email`. */
const SIGN_IN_LINK_ACTION_QUERY_KEYS = ['apiKey', 'oobCode', 'mode', 'lang'] as const;

export function hrefHasSignInLinkAction(href: string, origin = 'http://localhost'): boolean {
  const url = new URL(href, origin);
  return url.searchParams.has('oobCode') || url.searchParams.get('mode') === 'signIn';
}

/** Path + search + hash with the one-time action code removed (email hint kept). */
export function hrefWithoutSignInLinkAction(href: string, origin = 'http://localhost'): string {
  const url = new URL(href, origin);
  if (!hrefHasSignInLinkAction(href, origin)) {
    return `${url.pathname}${url.search}${url.hash}`;
  }
  for (const key of SIGN_IN_LINK_ACTION_QUERY_KEYS) {
    url.searchParams.delete(key);
  }
  const search = url.searchParams.toString();
  return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
}

/**
 * Clean `/login` URL once the link signed in: drop the action code and email
 * hint, keep `returnTo` — LoginPage reads it from the URL to route the
 * post-login redirect (e.g. back to /purchase for a new buyer, #7782).
 */
export function loginHrefAfterSignInLink(href: string, origin = 'http://localhost'): string {
  const returnTo = new URL(href, origin).searchParams.get('returnTo');
  return returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login';
}

export function replaceSignInLinkActionInHistory(): void {
  if (typeof window === 'undefined') return;
  const next = hrefWithoutSignInLinkAction(window.location.href, window.location.origin);
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next === current) return;
  window.history.replaceState({}, document.title, next);
}

/** True when Google (or another method) already signed in and the leftover oobCode is dead. */
export function isStaleSignInLinkError(
  error: { code?: string },
  currentUser: { uid?: string } | null | undefined
): boolean {
  if (!currentUser?.uid) return false;
  return error.code === 'auth/invalid-action-code' || error.code === 'auth/expired-action-code';
}
